import differenceInDays from "date-fns/differenceInDays";
import startOfToday from "date-fns/startOfToday";
import { dateTimeToView } from "helpers/date";
import { regionTypesEnum, sourceTypesEnum } from "helpers/enums";
import { alphabeticalSort, ymEvent } from "helpers/system";
import { useLocalStorage } from "hooks/useLocalStorage";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useValue } from "../realtime";
import CityReport from "./report";

const formatters = {
    withoutBidPeriod: (item) => -moment.duration(item).asSeconds(),
    planEndDate: (item) => moment(item),
    estimatedLeaveDate: (item) => moment(item),
};

const defaultFilters = {
    carId: null,
    bidTrailerTypeId: null,
    carLogistId: null,
    responsibleId: null,
    locationId: null,
    transportColumn: null,
    onlyWithExternalBids: null,
    getNextPlannedBid: null,
    bidTrailerId: null,
    carGroupId: null,
    bidDriverId: null,
    accessPermitIds: null,
    regionGroupId: null,
    bidPointLoadUnloadStatus: null,
    loadUnloadDate: "",
};

function getForwardingEnabled(profile) {
    return profile?.availableFeatureModules?.includes(sourceTypesEnum.Forwarding);
}

export default function CityReportContainer(props) {
    const {
        profile,
        data,
        params,
        organization,
        setParam,
        refreshData,
        history,
        roles,
        modal,
        enums,
        resetRequestParams,
        withoutPagination,
        disableCarDropd,
        disableFilters,
        disableRefresh,
    } = props;

    const realtimeData = useValue({
        listenEventNames: ["data[]"],
        originalValue: data,
    });

    const forwardingEnabled = useMemo(() => getForwardingEnabled(profile), [profile]);

    const [isLoading, setIsLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [kindFilter, setKindFilter] = useState(null);
    const [sorted, setSorted] = useState({
        column: "state",
        direction: "asc",
        field: null,
    });
    const filtersStorageKey = props.filtersStorageKey || "planningTableFilters";
    const initialFilters = props.initialFilters || defaultFilters;
    const [storedFilters, setStoredFilters] = useLocalStorage(filtersStorageKey, initialFilters);
    const [localFilters, setLocalFilters] = useState(initialFilters);
    const filters = props.disableStoredFilters ? localFilters : storedFilters;
    const setFilters = props.disableStoredFilters ? setLocalFilters : setStoredFilters;

    const stateRef = useRef({ filters, kindFilter, sorted, items });

    useEffect(() => {
        stateRef.current = { filters, kindFilter, sorted, items };
    }, [filters, kindFilter, sorted, items]);

    const [counters, setCounters] = useState({
        hasPlannedBid: 0,
        withBid: 0,
        withoutBidPeriod: 0,
        isLastBidPointVisited: 0,
        withBookingComment: 0,
        carsCount: {},
    });

    // componentDidMount
    useEffect(() => {
        ymEvent("PlanningCityOpened");
    }, []);

    // recalc items on data change
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const { sorted: currentSorted, filters: currentFilters, kindFilter: currentKindFilter } = stateRef.current;
            const { column, direction, field } = currentSorted;
            const newItems = sortedItems(formatItems(realtimeData.data), column, field, direction);
            setItems(newItems);
            setFilteredItems(
                filterItems(newItems, { ...currentFilters, kindFilter: currentKindFilter, items: newItems }),
            );
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [realtimeData.data]);

    // Helpers
    const refreshCounters = useCallback(
        (items) => {
            const newCounters = {
                hasPlannedBid: 0,
                withBid: 0,
                withoutBidPeriod: 0,
                isLastBidPointVisited: 0,
                withBookingComment: 0,
                carsCount: {},
            };
            const { columns } = realtimeData;
            items.forEach(({ city, state, country, countryId, federalDistrictId, stateId, timezone, ...other }) => {
                Object.values(other).forEach((column) => {
                    column.cars.forEach((carData) => {
                        newCounters[carData.kind]++;
                    });
                });
            });
            columns.forEach((c) => {
                newCounters.carsCount[c.index] = items
                    .filter((x) => x[c.index])
                    .reduce((sum, current) => sum + current[c.index].cars.length, 0);
            });
            setCounters(newCounters);
        },
        [realtimeData],
    );

    const kindValue = useCallback((car) => {
        if (car.bookingComment) {
            return "withBookingComment";
        } else if (car.hasPlannedBid) {
            return "hasPlannedBid";
        } else if (car.withoutBidPeriod) {
            return "withoutBidPeriod";
        } else if (car.isLastBidPointVisited) {
            return "isLastBidPointVisited";
        } else if (!car.isLastBidPointVisited) {
            return "withBid";
        }
    }, []);

    const formatItems = useCallback(
        (dataArr) => {
            const result = (dataArr ?? []).map(
                ({
                    city,
                    state,
                    country,
                    factOrPlanBidEndDates,
                    planDistributionBidStartDates,
                    countryId,
                    federalDistrictId,
                    stateId,
                }) => {
                    const dateData = {};
                    factOrPlanBidEndDates.forEach((car) => {
                        car = {
                            ...car,
                            regionId: stateId,
                            regionType: regionTypesEnum.FederalSubject,
                            kind: kindValue(car),
                        };
                        if (!dateData[car.index]) {
                            dateData[car.index] = { cars: [], bids: [] };
                        }
                        dateData[car.index].cars.push(car);
                    });
                    const timezone = factOrPlanBidEndDates[0]
                        ? dateTimeToView(factOrPlanBidEndDates[0].planEndDate, "(Z)")
                        : "";
                    (planDistributionBidStartDates || []).forEach((bid) => {
                        if (!dateData[bid.index]) {
                            dateData[bid.index] = { cars: [], bids: [] };
                        }
                        dateData[bid.index].bids.push(bid);
                    });
                    return { city, state, country, countryId, federalDistrictId, stateId, timezone, ...dateData };
                },
            );
            return result;
        },
        [kindValue],
    );

    // Filtering helpers
    const filterRowData = useCallback((columns, filterFunc) => {
        const filteredByCar = {};
        let rowHasData = false;
        Object.entries(columns).forEach(([columnIndex, data]) => {
            const filteredRow = data.cars.filter(filterFunc);
            if (filteredRow.length > 0) {
                rowHasData = true;
            }
            if (!filteredByCar[columnIndex]) {
                filteredByCar[columnIndex] = {};
            }
            filteredByCar[columnIndex] = { cars: filteredRow, bids: data.bids };
        });
        return { rowHasData, filteredByCar };
    }, []);

    const filteredByLocationProp = useCallback(
        (list, value) => list.filter(({ city, state }) => value.includes(state) || value.includes(city)),
        [],
    );

    const filteredByCarProp = useCallback(
        (list, prop, value) => {
            return list.reduce(
                (result, { city, state, country, countryId, federalDistrictId, stateId, timezone, ...columns }) => {
                    const { rowHasData, filteredByCar } = filterRowData(columns, (item) =>
                        Array.isArray(value) ? value.includes(item[prop] || 0) : value === item[prop],
                    );
                    if (rowHasData) {
                        result.push({
                            city,
                            state,
                            country,
                            countryId,
                            federalDistrictId,
                            stateId,
                            timezone,
                            ...filteredByCar,
                        });
                    }
                    return result;
                },
                [],
            );
        },
        [filterRowData],
    );

    const filteredByCarGroups = useCallback(
        (list, value) => {
            return list.reduce(
                (result, { city, state, country, countryId, federalDistrictId, stateId, timezone, ...columns }) => {
                    const { rowHasData, filteredByCar } = filterRowData(columns, (item) => {
                        const propValue = item.carGroups.map((item) => item.id);
                        return value.some((val) => propValue.includes(val));
                    });
                    if (rowHasData) {
                        result.push({
                            city,
                            state,
                            country,
                            countryId,
                            federalDistrictId,
                            stateId,
                            timezone,
                            ...filteredByCar,
                        });
                    }
                    return result;
                },
                [],
            );
        },
        [filterRowData],
    );

    const filteredByAccessPermits = useCallback(
        (list, value) => {
            return list.reduce(
                (result, { city, state, country, countryId, federalDistrictId, stateId, timezone, ...columns }) => {
                    const { rowHasData, filteredByCar } = filterRowData(columns, (item) =>
                        value.every(
                            (val) =>
                                item.carAccessPermitIds.includes(val) ||
                                item.driverAccessPermitIds.includes(val) ||
                                item.trailerAccessPermitIds.includes(val),
                        ),
                    );
                    if (rowHasData) {
                        result.push({
                            city,
                            state,
                            country,
                            countryId,
                            federalDistrictId,
                            stateId,
                            timezone,
                            ...filteredByCar,
                        });
                    }
                    return result;
                },
                [],
            );
        },
        [filterRowData],
    );

    const filteredByExternalBids = useCallback(
        (list) => {
            return list.reduce(
                (result, { city, state, country, countryId, federalDistrictId, stateId, timezone, ...columns }) => {
                    const { rowHasData, filteredByCar } = filterRowData(
                        columns,
                        (item) => item.carExternalOutput != null,
                    );
                    if (rowHasData) {
                        result.push({
                            city,
                            state,
                            country,
                            countryId,
                            federalDistrictId,
                            stateId,
                            timezone,
                            ...filteredByCar,
                        });
                    }
                    return result;
                },
                [],
            );
        },
        [filterRowData],
    );

    const filteredByLoadUnloadDate = useCallback((list, loadUnloadDate) => {
        return list.reduce(
            (result, { city, state, country, countryId, federalDistrictId, stateId, timezone, ...columns }) => {
                const daysBetween = differenceInDays(new Date(loadUnloadDate), startOfToday());
                const filteredByDay = {};
                let rowHasData = false;
                Object.entries(columns).forEach(([columnIndex, data]) => {
                    if (!filteredByDay[columnIndex]) {
                        filteredByDay[columnIndex] = {};
                    }
                    if (Number(columnIndex) === daysBetween) {
                        rowHasData = data.cars.length > 0;
                        filteredByDay[columnIndex] = data;
                    } else {
                        filteredByDay[columnIndex] = { cars: [], bids: [] };
                    }
                });
                if (rowHasData) {
                    result.push({
                        city,
                        state,
                        country,
                        countryId,
                        federalDistrictId,
                        stateId,
                        timezone,
                        ...filteredByDay,
                    });
                }
                return result;
            },
            [],
        );
    }, []);

    const filteredByRegionGroups = useCallback((list, value) => {
        return list.filter(({ stateId }) => value.regionIds.includes(stateId));
    }, []);

    // Main filterItems
    const filterItems = useCallback(
        (data, localState) => {
            let items = data || localState.items;
            if (localState.locationId) {
                items = filteredByLocationProp(items, localState.locationId);
            }
            if (localState.regionGroupId) {
                items = filteredByRegionGroups(items, localState.regionGroupId);
            }
            if (localState.kindFilter) {
                items = filteredByCarProp(items, "kind", localState.kindFilter);
            }
            if (localState.carLogistId) {
                items = filteredByCarProp(items, "carLogistId", localState.carLogistId);
            }
            if (localState.responsibleId) {
                items = filteredByCarProp(items, "responsibleId", localState.responsibleId);
            }
            if (localState.carId) {
                items = filteredByCarProp(items, "carId", localState.carId);
            }
            if (localState.bidTrailerTypeId) {
                items = filteredByCarProp(items, "bidTrailerTypeId", localState.bidTrailerTypeId);
            }
            if (localState.bidPointLoadUnloadStatus) {
                items = filteredByCarProp(items, "bidPointLoadUnloadStatus", localState.bidPointLoadUnloadStatus);
            }
            if (localState.bidTrailerId) {
                items = filteredByCarProp(items, "bidTrailerId", localState.bidTrailerId);
            }
            if (localState.carGroupId) {
                items = filteredByCarGroups(items, localState.carGroupId);
            }
            if (localState.bidDriverId) {
                items = filteredByCarProp(items, "bidDriverId", localState.bidDriverId);
            }
            if (localState.accessPermitIds) {
                items = filteredByAccessPermits(items, localState.accessPermitIds);
            }
            if (localState.transportColumn) {
                items = filteredByCarProp(items, "transportColumnId", localState.transportColumn);
            }
            if (localState.onlyWithExternalBids) {
                items = filteredByExternalBids(items);
            }
            if (localState.loadUnloadDate) {
                items = filteredByLoadUnloadDate(items, localState.loadUnloadDate);
            }
            refreshCounters(items);
            return items;
        },
        [
            filteredByLocationProp,
            filteredByRegionGroups,
            filteredByCarProp,
            filteredByCarGroups,
            filteredByAccessPermits,
            filteredByExternalBids,
            filteredByLoadUnloadDate,
            refreshCounters,
        ],
    );

    const onMultipleFilterChange = useCallback(
        (dataArr) => {
            const updates = dataArr.reduce((acc, { name, value }) => {
                acc[name] = value && Array.isArray(value) ? (value.length ? value : null) : value;
                return acc;
            }, {});

            setIsLoading(true);

            let newKindFilter = kindFilter;
            const newFilters = { ...filters };
            let filtersChanged = false;

            Object.entries(updates).forEach(([key, val]) => {
                if (key === "kindFilter") {
                    newKindFilter = val;
                    setKindFilter(val);
                } else {
                    newFilters[key] = val;
                    filtersChanged = true;
                }
            });

            if (filtersChanged) {
                setFilters(newFilters);
            }

            setTimeout(() => {
                const currentItems = stateRef.current.items;
                const combinedState = { ...newFilters, kindFilter: newKindFilter, items: currentItems };
                setFilteredItems(filterItems(undefined, combinedState));
                setIsLoading(false);
            }, 500);
        },
        [filterItems, filters, kindFilter],
    );

    // Filter change handlers
    const onFilterChange = useCallback((data) => onMultipleFilterChange([data]), [onMultipleFilterChange]);

    // Sorting
    const sortedItems = useCallback((items, id, field, direction) => {
        return items.slice().sort((a, b) => {
            let result;
            if (field) {
                if (!a[id]) {
                    return 1;
                }
                if (!b[id]) {
                    return -1;
                }
                const formatter = formatters[field];
                if (direction === "desc") {
                    a[id].cars.sort((c, d) => formatter(d[field]) - formatter(c[field]));
                    b[id].cars.sort((c, d) => formatter(d[field]) - formatter(c[field]));
                    if (!a[id].cars[0] && !b[id].cars[0]) {
                        result = 0;
                    } else if (!a[id].cars[0]) {
                        result = 1;
                    } else if (!b[id].cars) {
                        result = -1;
                    } else {
                        const maxA = a[id].cars[0][field];
                        const maxB = b[id].cars[0][field];
                        result = formatter(maxB) - formatter(maxA);
                    }
                } else {
                    a[id].cars.sort((c, d) => formatter(c[field]) - formatter(d[field]));
                    b[id].cars.sort((c, d) => formatter(c[field]) - formatter(d[field]));
                    if (!a[id].cars[0] && !b[id].cars[0]) {
                        result = 0;
                    } else if (!a[id].cars[0]) {
                        result = 1;
                    } else if (!b[id].cars) {
                        result = -1;
                    } else {
                        const minA = a[id].cars[0][field];
                        const minB = b[id].cars[0][field];
                        result = formatter(minA) - formatter(minB);
                    }
                }
                return result;
            } else {
                result = alphabeticalSort(a, b, id);
                return direction === "desc" ? -result : result;
            }
        });
    }, []);

    const sortBy = useCallback(
        (column, field) => {
            const { id } = column;
            const direction =
                sorted.column === id && sorted.field === field && sorted.direction === "asc" ? "desc" : "asc";
            const newItems = sortedItems(filteredItems, column.id, field, direction);
            setFilteredItems(newItems);
            setSorted({ column: id, field, direction });
        },
        [sorted, filteredItems, sortedItems],
    );

    const resetFilters = useCallback(() => {
        setFilters(initialFilters);
        resetRequestParams();
    }, [initialFilters, resetRequestParams, setFilters]);

    const reportProps = {
        organization,
        resetFilters,
        hasFilters:
            Object.keys(defaultFilters).some((key) => !!filters[key]) ||
            Object.keys(params).some((key) => !!params[key]),
        columns: realtimeData.columns,
        kindFilter,
        filteredItems,
        isLoading,
        params,
        toggleParam: (param) => setParam(param, !params[param]),
        setParam,
        data: realtimeData,
        ...filters,
        setValue: onFilterChange,
        setMultipleValue: onMultipleFilterChange,
        counters,
        refreshData,
        forwardingEnabled,
        history,
        roles,
        modal,
        enums,
        sortBy,
        sorted,
        withoutPagination,
        disableCarDropd,
        disableFilters,
        disableRefresh,
        isTour: props.isTour,
    };

    return <CityReport {...reportProps} />;
}
